import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  GlcodeService, ApiVisa, GlcodeContainerModel, Pagination
} from 'blg-akaun-ts-lib';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, tap, filter, switchMap, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-select-glcode-drop-down',
  templateUrl: './select-glcode-drop-down.component.html',
  styleUrls: ['./select-glcode-drop-down.component.scss']
})

export class ErpSelectGlcodeDropDownComponent implements OnInit, OnDestroy {

  @Input() apiVisa: ApiVisa;
  @Input() glcode = new FormControl();
  @Input() coaGuid: string;
  @Input() label: string;

  @Output() glcodeSelected = new EventEmitter<GlcodeContainerModel>();
  @Output() glcodeChange = new EventEmitter<FormControl>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<GlcodeContainerModel[]> = new ReplaySubject<GlcodeContainerModel[]>(1);
  glcodeList: GlcodeContainerModel[] = [];
  selectedGlcode: GlcodeContainerModel;

  protected _onDestroy = new Subject<void>();

  constructor(
    private glcodeService: GlcodeService
  ) {
  }

  ngOnInit() {
    setTimeout(() => {
      //console.log('glcode', this.glcode.value);
    if (this.glcode.value) {
      this.onPatchValue(this.glcode.value);
    }}, 1500);
    this.serverSideFiltering.valueChanges.pipe(
        filter(searchText => searchText.length > 3),
        tap(() => this.serverSideSearching = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchText => this.search(searchText))
      ).subscribe( resolved => {
        this.serverSideSearching = false;
        if (this.selectedGlcode) {
          this.filteredOptions$.next([...resolved, this.selectedGlcode]);
          this.glcodeList = [...resolved, this.selectedGlcode];
        } else {
          this.filteredOptions$.next(resolved);
          this.glcodeList = resolved;
        }
      }, error => {
        this.serverSideSearching = false;
        console.error(error);
      });
  }

  /**
   * Server-side GL code typeahead.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   SELECT hdr.guid FROM bl_fi_mst_glcode AS hdr
   *   WHERE hdr.status != 'DELETED'
   *     AND hdr.chart_of_acc_guid = '<coaGuid>'
   *     AND (hdr.gl_code_1 ILIKE '%<text>%' OR hdr.name ILIKE '%<text>%')
   *   ORDER BY hdr.gl_code_1
   *
   * GET glcode/query -> GlcodeUow.getByCriteria -> generateWhereClauseByCriteria
   * honours every part of it as criteria: `search_word` (bound, ILIKE),
   * `chart_of_acc_guid`, and a default of `status != 'DELETED'` when no status is
   * given. orderBy/order reproduce `ORDER BY hdr.gl_code_1` (that UoW defaults to
   * DESC, so `order` must be sent explicitly).
   *
   * Behaviour notes:
   * - `search_word` also ILIKEs gl_code_2..4, descr and txn_type, so the typeahead
   *   matches a little more than the old gl_code_1/name pair.
   * - One request now instead of one subquery plus one getByGuid per hit, and the
   *   container comes back from the same query rather than a follow-up fetch.
   */
  search(searchText: string): Observable<GlcodeContainerModel[]> {
    if (!this.coaGuid) {
      return of([]);
    }
    const pagination = new Pagination(
      0,
      100,
      [
        { columnName: 'chart_of_acc_guid', operator: '=', value: this.coaGuid },
        { columnName: 'search_word', operator: '=', value: searchText.trim() }
      ],
      [
        { columnName: 'orderBy', value: 'gl_code_1' },
        { columnName: 'order', value: 'ASC' }
      ]
    );
    return this.glcodeService.getByCriteria(pagination, this.apiVisa).pipe(
      map(response => response.data)
    );
  }

  onSelect(guid: string) {
    this.glcode.patchValue(guid);
    this.selectedGlcode = this.glcodeList.find( glcode => glcode.bl_fi_mst_glcode.guid === guid);
    this.glcodeSelected.emit(this.selectedGlcode);
  }

  onPatchValue(guid: string) {
    this.glcodeService.getByGuid(guid, this.apiVisa).pipe(
      takeUntil(this._onDestroy)
    ).subscribe( resolved => {
      this.glcodeList = [resolved.data];
      this.filteredOptions$.next([resolved.data]);
      this.onSelect(resolved.data.bl_fi_mst_glcode.guid.toString());
    });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
